import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { format } from 'date-fns';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import usePageMeta from '@/utils/usePageMeta';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageMeta({
    title: blog ? `${blog.title} | DataLogicHub Blog` : 'Blog | DataLogicHub',
    description: blog?.excerpt || blog?.summary,
  });

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);

        const docRef = doc(db, 'blog_posts', id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError('Blog post not found');
          return;
        }

        const post = { id: docSnap.id, ...docSnap.data() };

        const publishedAt =
          post.createdAt?.toDate?.().toISOString() ||
          post.publishedAt?.toDate?.().toISOString() ||
          post.created_at ||
          new Date().toISOString();

        const coverImage =
          post.imageUrl || post.image_url || post.coverImage || null;
        const videoUrl = post.videoUrl || null;

        // Strip HTML meta comments like <!--meta: ...--> or <!==meta: ...-->
        const rawContent = post.content || post.body || '';
        const cleanContent = rawContent.replace(/<!--[\s\S]*?-->/g, '').replace(/<!==.*?(?:-->|$)/gm, '').trim();

        const transformedBlog = {
          id: post.id,
          title: post.title || 'Untitled',
          content: cleanContent,
          coverImage,
          videoUrl,
          category: post.category || post.platform || 'General',
          publishedAt,
          readTime: Math.ceil(((post.content || post.body || '')?.length || 0) / 1000) || 1,
        };

        setBlog(transformedBlog);
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id]);

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(blog?.title || '');
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0f4f8] to-[#e0e7ff] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#667eea] border-t-transparent mb-4"></div>
          <p className="text-[#6b7280] text-lg">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0f4f8] to-[#e0e7ff] py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-[#fee2e2] border border-[#ef4444] rounded-[15px] p-8">
            <h2 className="text-2xl font-bold text-[#991b1b] mb-4">Error Loading Post</h2>
            <p className="text-[#dc2626] mb-6">{error || 'Blog post not found'}</p>
            <button
              onClick={() => navigate('/blogs')}
              className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white px-6 py-3 rounded-[10px] font-semibold hover:opacity-90 transition-opacity"
            >
              Back to Blogs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = blog.publishedAt 
    ? format(new Date(blog.publishedAt), 'MMMM dd, yyyy')
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4f8] to-[#e0e7ff]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to="/blogs"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to all posts</span>
          </Link>

          {blog.category && (
            <div className="mb-4">
              <span className="inline-block px-3 py-1 rounded-[15px] text-xs font-bold uppercase bg-white/20 backdrop-blur-sm">
                {blog.category}
              </span>
            </div>
          )}

          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <span>{formattedDate}</span>
            </div>
            {blog.readTime && (
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{blog.readTime} min read</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cover Media */}
      {(blog.videoUrl || blog.coverImage) && (
        <div className="max-w-4xl mx-auto px-4 mt-8">
          <div className="rounded-[20px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.2)]">
            {blog.videoUrl ? (
              <video
                src={blog.videoUrl}
                controls
                className="w-full max-h-[480px] bg-black"
                preload="metadata"
              />
            ) : (
              <img
                src={blog.coverImage}
                alt={blog.title}
                className="w-full max-h-[480px] object-contain bg-black"
              />
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-[20px] shadow-[0_8px_25px_rgba(0,0,0,0.1)] p-8 md:p-12">
          {/* Social Share */}
          <div className="flex items-center justify-between border-b border-[#e5e7eb] pb-6 mb-8">
            <div className="flex items-center gap-2 text-[#6b7280]">
              <Share2 size={20} />
              <span className="font-medium">Share this post:</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleShare('facebook')}
                className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                aria-label="Share on Facebook"
              >
                <Facebook size={18} />
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                aria-label="Share on Twitter"
              >
                <Twitter size={18} />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={18} />
              </button>
            </div>
          </div>

          {/* Article Content */}
          <article className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-[#1f2937] mt-8 mb-4" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-3xl font-bold text-[#1f2937] mt-8 mb-4" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-bold text-[#374151] mt-6 mb-3" {...props} />,
                p: ({node, ...props}) => <p className="text-[#4b5563] leading-relaxed mb-4" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside text-[#4b5563] mb-4 space-y-2" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside text-[#4b5563] mb-4 space-y-2" {...props} />,
                blockquote: ({node, ...props}) => (
                  <blockquote className="border-l-4 border-[#667eea] bg-[#f3f4f6] px-6 py-4 my-6 italic text-[#374151]" {...props} />
                ),
                code: ({node, inline, className, children, ...props}) => {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={oneDark}
                      language={match[1]}
                      PreTag="div"
                      className="rounded-lg my-4 text-sm"
                      showLineNumbers
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : !inline ? (
                    <SyntaxHighlighter
                      style={oneDark}
                      PreTag="div"
                      className="rounded-lg my-4 text-sm"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className="bg-[#f3f4f6] text-[#667eea] px-2 py-1 rounded text-sm font-mono" {...props}>
                      {children}
                    </code>
                  );
                },
                img: ({node, ...props}) => (
                  <img className="rounded-lg my-6 w-full shadow-lg" {...props} />
                ),
                a: ({node, ...props}) => (
                  <a className="text-[#667eea] hover:text-[#764ba2] underline transition-colors" {...props} />
                ),
              }}
            >
              {blog.content}
            </ReactMarkdown>
          </article>
        </div>

        {/* Back to Blogs Button */}
        <div className="mt-8 text-center">
          <Link
            to="/blogs"
            className="inline-block bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white px-8 py-4 rounded-[10px] font-semibold hover:opacity-90 transition-opacity"
          >
            View All Posts
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
